/**
 * Purchase-format tag carried in from the Shopify entry link (`?tag=...`).
 *
 * Keep in sync with API/src/v1/utils/helpers/bookTag.js — the API is the
 * authority (it sanitizes before persisting); this mirror keeps an unknown tag
 * in the URL from changing what the client does before that round trip.
 */

const BOOK_TAGS = Object.freeze(["downloadPdf"]);

/** Returns the tag when it is one we recognise, otherwise null. */
export function sanitizeBookTag(tag) {
  if (typeof tag !== "string") return null;
  const trimmed = tag.trim();
  return BOOK_TAGS.includes(trimmed) ? trimmed : null;
}

/**
 * The book's purchase format. Prefers the persisted value so the choice survives
 * redirects, refreshes and re-logins; the URL is only a fallback for customers
 * already mid-funnel when this shipped.
 */
export function resolveBookTag(book, urlTag) {
  return sanitizeBookTag(book?.tag) || sanitizeBookTag(urlTag);
}

/**
 * Carries the tag across a programmatic redirect.
 *
 * The tag only reaches the DB once the customer names their book, so every
 * redirect between the Shopify landing and that first save has to preserve it —
 * a bare navigate() silently downgrades a PDF buyer to the physical SKU.
 */
export function withBookTag(destination, tag) {
  const safe = sanitizeBookTag(tag);
  if (!safe || typeof destination !== "string") return destination;
  if (/[?&]tag=/.test(destination)) return destination;
  return `${destination}${destination.includes("?") ? "&" : "?"}tag=${encodeURIComponent(safe)}`;
}

/** The tag currently in the address bar, if it is one we recognise. */
export function currentUrlBookTag() {
  if (typeof window === "undefined") return null;
  return sanitizeBookTag(new URLSearchParams(window.location.search).get("tag"));
}
